// ─── sessionExpiry.js ─────────────────────────────────────────────────────────
// Global axios response interceptor that catches expired-session responses
// (HTTP 401) from the API, clears local auth state, stores a one-shot flash
// message in sessionStorage, and redirects the user to the login page.
//
// Why this exists:
//   After the JWT expires, every API call returns 401 and individual screens
//   render raw "Unable to load …" errors with Retry buttons. That looks like
//   the app is broken. This module centralises the recovery flow so the
//   user lands on /login with a friendly "Your session has expired, please
//   log in again." banner instead.
//
// Design notes:
//   - Idempotent install via `__sessionExpiryInstalled` flag — calling
//     installSessionExpiryInterceptor() more than once is a no-op.
//   - Auth endpoints (login, refresh, 2FA verify, password reset) are
//     EXEMPT from the redirect so invalid-credential responses stay inline
//     on the login form rather than triggering a loop.
//   - We guard against multiple concurrent redirects with a module-level
//     `redirecting` flag — a single expired token typically fires several
//     parallel API calls and we want exactly one redirect.
//   - The flash uses sessionStorage (not localStorage) so it auto-clears
//     when the tab closes and does NOT survive a real fresh login.
// ──────────────────────────────────────────────────────────────────────────────

import axios from 'axios'

export const SESSION_EXPIRED_FLASH_KEY = 'session_expired_flash'

// Endpoints that legitimately return 401 during normal auth flow — do NOT
// redirect on these, otherwise a wrong-password attempt would bounce the
// user away from the login form.
const AUTH_EXEMPT_PATTERNS = [
    /\/api\/login\/?$/,
    /\/api\/2fa\/verify\/?$/,
    /\/api\/2fa\/email\/resend\/?$/,
    // The real refresh endpoint (was mistakenly /api/token/refresh/). A 401 here
    // means the refresh token itself is dead → fall through to a real logout,
    // don't try to refresh the refresh call.
    /\/api\/refresh-token\/?$/,
    // Silent SSO probes: a 401 just means "no Django session" — it must NOT look
    // like an expired session. ProtectedRoute handles the no-session fallback.
    /\/api\/session-token\/?$/,
    /\/api\/sso-token\/?$/,
    /\/api\/password-reset/,
    /\/api\/register/,
    /\/api\/account\/change-password\/?$/,
]

// ─── Silent access-token refresh ────────────────────────────────────────────────
// The access token is short-lived (24h, or 720h with Remember Me) but the refresh
// token lasts 30 days. Without this, the first API 401 after the access token
// expires logs an ACTIVE user out. On a 401 we transparently exchange the refresh
// token for a new access token and replay the original request.
const JWT_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
const REFRESH_KEY = import.meta.env.VITE_REFRESH_TOKEN_KEY || 'refresh_token'
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000'

// Which storage this session uses — mirrors auth-store: remember='0' →
// sessionStorage, otherwise localStorage.
function sessionStore() {
    try {
        return localStorage.getItem('wq_remember_me') === '0' ? sessionStorage : localStorage
    } catch (_e) {
        return localStorage
    }
}
// Read a token the same way the lib/*.js helpers do: localStorage first.
function readStored(key) {
    try { return localStorage.getItem(key) || sessionStorage.getItem(key) } catch (_e) { return null }
}
// Persist refreshed tokens into the active store, and clear the other store so a
// stale copy there can't shadow the fresh token on the next read.
function writeTokens(access, refresh) {
    try {
        const store = sessionStore()
        const other = store === localStorage ? sessionStorage : localStorage
        store.setItem(JWT_KEY, access)
        other.removeItem(JWT_KEY)
        if (refresh) { store.setItem(REFRESH_KEY, refresh); other.removeItem(REFRESH_KEY) }
    } catch (_e) { /* ignore storage failures */ }
}

// Single-flight: many requests 401 at once when the token expires, but the
// backend ROTATES the refresh token on every call — so a second concurrent
// refresh would send a now-stale token and fail. Share one in-flight refresh.
let refreshPromise = null
function refreshAccessToken() {
    if (refreshPromise) return refreshPromise
    const refresh = readStored(REFRESH_KEY)
    if (!refresh) return Promise.reject(new Error('no refresh token'))
    refreshPromise = axios
        .post(`${API_BASE}/api/refresh-token/`, { refresh_token: refresh })
        .then((res) => {
            const access = res?.data?.access_token
            if (!access) throw new Error('no access token in refresh response')
            writeTokens(access, res?.data?.refresh_token)
            return access
        })
        .finally(() => { refreshPromise = null })
    return refreshPromise
}

// Single-flight session-cookie SSO (mirrors auth-store.sessionLogin). A FRESH
// TAB has no refresh token yet, but the browser may still carry a valid Django
// session (e.g. the user is logged in in another tab of the same window). Mint
// JWTs from that session and persist them honouring wq_remember_me, so an
// in-flight API 401 can be replayed instead of bouncing the tab to /login.
// This is what makes a new tab stay logged in. Resolves to null when there is
// no live session (the /api/session-token/ 401 is itself auth-exempt, so it
// never recurses here).
let sessionExchangePromise = null
function sessionTokenExchange() {
    if (sessionExchangePromise) return sessionExchangePromise
    sessionExchangePromise = axios
        .get(`${API_BASE}/api/session-token/`, { withCredentials: true })
        .then((res) => {
            const data = res?.data
            if (!data || !data.success || !data.access_token) return null
            writeTokens(data.access_token, data.refresh_token)
            try {
                const remember = localStorage.getItem('wq_remember_me')
                const store = remember === '0' ? sessionStorage : localStorage
                if (data.user) store.setItem('auth_user', JSON.stringify(data.user))
                if (remember === null) localStorage.setItem('wq_remember_me', '1')
            } catch (_e) { /* ignore storage failures */ }
            return data.access_token
        })
        .catch(() => null)
        .finally(() => { sessionExchangePromise = null })
    return sessionExchangePromise
}

// Paths where we should NOT trigger a redirect (already on auth flow).
const AUTH_PATHS = ['/login', '/reset-password', '/setup-2fa']

// Routes served without authentication — 401s that fire from widgets on
// these pages should be swallowed silently (widget shows an empty state)
// instead of tearing the whole page down to /login.
const PUBLIC_PAGE_PREFIXES = ['/public/']

let redirecting = false

function isAuthExemptUrl(url) {
    if (!url) return false
    return AUTH_EXEMPT_PATTERNS.some((re) => re.test(url))
}

function isOnAuthPage() {
    const path = window.location.pathname || ''
    return AUTH_PATHS.some((p) => path.startsWith(p))
}

function isOnPublicPage() {
    const path = window.location.pathname || ''
    return PUBLIC_PAGE_PREFIXES.some((p) => path.startsWith(p))
}

function clearAuthStorage() {
    const JWT_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
    const REFRESH_KEY = import.meta.env.VITE_REFRESH_TOKEN_KEY || 'refresh_token'
    try {
        ;[localStorage, sessionStorage].forEach(s => {
            s.removeItem(JWT_KEY)
            s.removeItem(REFRESH_KEY)
            s.removeItem('auth_user')
        })
        localStorage.removeItem('wq_remember_me')
    } catch (_e) {
        /* storage can throw in some private-mode browsers — ignore */
    }
}

/**
 * Handle a confirmed expired/invalid-session response by clearing auth
 * state, storing the flash message, and redirecting to the login page.
 * Safe to call multiple times — only the first call performs the redirect.
 */
export function handleSessionExpired(message) {
    if (redirecting) return
    redirecting = true

    clearAuthStorage()

    try {
        sessionStorage.setItem(
            SESSION_EXPIRED_FLASH_KEY,
            message || 'Your session has expired. Please log in again.'
        )
    } catch (_e) {
        /* ignore storage failures */
    }

    // Use a full navigation rather than React Router so all in-flight axios
    // calls, intervals, and stores are torn down cleanly.
    if (!isOnAuthPage()) {
        window.location.replace('/login')
    }
}

/**
 * Install the global axios response interceptor. Idempotent — extra calls
 * are no-ops. Call this once at app boot, BEFORE the router renders.
 */
export function installSessionExpiryInterceptor() {
    if (typeof window === 'undefined') return
    if (window.__sessionExpiryInstalled) return
    window.__sessionExpiryInstalled = true

    axios.interceptors.response.use(
        (response) => response,
        async (error) => {
            const status = error?.response?.status
            const config = error?.config || {}
            const url = config.url || ''

            // Only act on 401 from non-auth-flow endpoints, and only when
            // the user appears to be in an authenticated context. Public
            // routes (/public/<pid>) are anonymous by design — a 401 from
            // an authed endpoint reached by a public widget must NOT
            // redirect the whole page; the widget just renders empty.
            if (status === 401 && !isAuthExemptUrl(url) && !isOnAuthPage() && !isOnPublicPage()) {
                // `_retry` prevents a loop if a replayed request also 401s.
                if (!config._retry) {
                    config._retry = true
                    // 1) Silent access-token refresh — keeps an active user logged
                    //    in when only the short-lived access token expired.
                    if (readStored(REFRESH_KEY)) {
                        try {
                            const access = await refreshAccessToken()
                            config.headers = config.headers || {}
                            config.headers.Authorization = `Bearer ${access}`
                            return axios(config)
                        } catch (_e) {
                            /* refresh failed → fall through to session SSO */
                        }
                    }
                    // 2) Silent session-cookie SSO — a fresh tab with no refresh
                    //    token but a live Django session (logged in elsewhere in
                    //    the same browser) mints a token and replays, instead of
                    //    being bounced to /login. THIS is the cross-tab fix.
                    try {
                        const access = await sessionTokenExchange()
                        if (access) {
                            config.headers = config.headers || {}
                            config.headers.Authorization = `Bearer ${access}`
                            return axios(config)
                        }
                    } catch (_e) {
                        /* no live session → real logout below */
                    }
                }
                handleSessionExpired()
                // Swallow the rejection for non-auth endpoints — the page is
                // being torn down anyway and we don't want every screen's
                // error toast to flash before the redirect.
                return new Promise(() => {})
            }

            return Promise.reject(error)
        }
    )
}

/**
 * Read and clear the one-shot flash message. Returns null if none set.
 */
export function consumeSessionExpiredFlash() {
    try {
        const msg = sessionStorage.getItem(SESSION_EXPIRED_FLASH_KEY)
        if (msg) sessionStorage.removeItem(SESSION_EXPIRED_FLASH_KEY)
        return msg || null
    } catch (_e) {
        return null
    }
}
